import type { DesktopAudioFormat } from '@/lib/desktop-recording';

/** Native PCM feeds a silent MediaStream destination; never the user's speakers. */
export function createDesktopPcmStream(context: AudioContext, format: DesktopAudioFormat) {
  if (format.encoding !== 's16le' || format.sampleRate !== 48000 || format.channels !== 2)
    throw new Error('unsupported_pcm_format');
  const destination = context.createMediaStreamDestination();
  const playing = new Set<AudioBufferSourceNode>();
  const stride = format.channels * 2;
  let remainder = new Uint8Array(0);
  let nextAt = 0;
  let stopped = false;
  return {
    stream: destination.stream,
    push(data: ArrayBuffer) {
      if (stopped) return;
      // IPC chunks are bytes, not guaranteed to end on a sample boundary.
      const bytes = new Uint8Array(remainder.length + data.byteLength);
      bytes.set(remainder);
      bytes.set(new Uint8Array(data), remainder.length);
      const frames = Math.floor(bytes.length / stride);
      remainder = bytes.slice(frames * stride);
      if (!frames) return;
      if (frames > format.sampleRate * 2 || nextAt - context.currentTime > 2)
        throw new Error('native_audio_backlog');
      const buffer = context.createBuffer(format.channels, frames, format.sampleRate);
      const view = new DataView(bytes.buffer);
      for (let channel = 0; channel < format.channels; channel++) {
        const samples = buffer.getChannelData(channel);
        for (let frame = 0; frame < frames; frame++)
          samples[frame] = view.getInt16(frame * stride + channel * 2, true) / 32768;
      }
      const node = context.createBufferSource();
      node.buffer = buffer;
      node.connect(destination);
      node.onended = () => {
        playing.delete(node);
        node.disconnect();
      };
      playing.add(node);
      nextAt = Math.max(nextAt, context.currentTime + 0.03);
      node.start(nextAt);
      nextAt += buffer.duration;
    },
    stop() {
      stopped = true;
      remainder = new Uint8Array(0);
      for (const node of playing) {
        node.stop();
        node.disconnect();
      }
      playing.clear();
      destination.disconnect();
    },
  };
}
